import React, {useState} from 'react';
import {normalizeTimeValue, TIME_FORMATS} from '../../utility/questionnaireFieldUtils';

const timeParts = {
  'HH:MM:SS': [['HH', 'Hours'], ['MM', 'Minutes', 59], ['SS', 'Seconds', 59]],
  'MM:SS': [['MM', 'Minutes'], ['SS', 'Seconds', 59]],
  'MM:SS.hh': [['MM', 'Minutes'], ['SS', 'Seconds', 59], ['hh', '1/100s of a second', 99]],
  'SS.hh': [['SS', 'Seconds'], ['hh', '1/100s of a second', 99]],
};
const blankPart = value => value == null || (typeof value === 'string' && value.trim() === '');
const partKeys = ['HH', 'MM', 'SS', 'hh'];
const partsFor = format => Object.prototype.hasOwnProperty.call(timeParts, format) ? timeParts[format] : undefined;

export const timeAnswerError = (question, value) => {
  if (blankPart(value)) return question.required ? 'Enter a time' : '';
  if (typeof value !== 'string' && (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).some(key => !['format', ...partKeys].includes(key)))) return 'Enter a valid time';
  if (typeof value === 'object' && typeof value.format !== 'string') return 'Select a time format';
  const time = normalizeTimeValue(value);
  const parts = partsFor(time.format);
  if (!parts && time.format !== '') return 'Select a time format';
  if (typeof value === 'object' && partKeys.every(key => blankPart(time[key])))
    return question.required ? 'Enter a time' : '';
  if (!parts) return 'Select a time format';
  for (const key of partKeys) {
    if (!blankPart(time[key]) && !((typeof time[key] === 'string' || typeof time[key] === 'number')
      && /^\d+$/.test(String(time[key])))) return 'Time values must be whole numbers of 0 or more';
  }
  for (const [key, label, max] of parts) {
    if (!blankPart(time[key]) && max !== undefined && Number(time[key]) > max)
      return `${label} must be between 0 and ${max}`;
  }
  return parts.some(([key]) => !blankPart(time[key])) ? '' : 'Enter a time';
};

export default function RegistrationTimeField({question, value, onChange, id}) {
  const [touched, setTouched] = useState(false);
  const time = normalizeTimeValue(value);
  const parts = partsFor(time.format) || [];
  const message = touched ? timeAnswerError(question, value) : '';
  const errorId = `${id}-error`;
  const describedBy = [question.helper ? `${id}-help` : null, message ? errorId : null].filter(Boolean).join(' ') || undefined;
  const props = {
    className: `form-control${message ? ' is-invalid' : ''}`,
    'aria-invalid': message ? true : undefined,
    'aria-describedby': describedBy,
    onBlur: () => setTouched(true),
  };
  return <div role="group" aria-labelledby={`${id}-label`}>
    <select {...props} id={id} value={time.format} required={question.required}
      onChange={event => {
        setTouched(true);
        const format = event.target.value;
        onChange(format ? {...normalizeTimeValue(''), format,
          ...Object.fromEntries(timeParts[format].map(([key]) => [key, time[key]]))} : '');
      }}>
      <option value="">Select time format</option>
      {TIME_FORMATS.map(format => <option key={format.value} value={format.value}>{format.label}</option>)}
    </select>
    <div className="row mt-2">
      {parts.map(([key, label, max]) => <div className="col-sm-4 mb-2" key={key}>
        <label htmlFor={`${id}-${key}`}>{label}</label>
        <input {...props} id={`${id}-${key}`} type="number" inputMode="numeric" min="0" max={max} step="1"
          placeholder={key} value={typeof time[key] === 'object' ? '' : time[key]}
          onChange={event => { setTouched(true); onChange({...time, [key]: event.target.value}); }} />
      </div>)}
    </div>
    {message && <div id={errorId} className="invalid-feedback d-block">{message}</div>}
  </div>;
}
